// src/components/features/analysis/ArtifactsTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../../services/api';
import { usePivot } from '../../../hooks/usePivot';
import PivotResults from './PivotResults';
import LoadingSpinner from '../../common/LoadingSpinner';
import ErrorMessage from '../../common/ErrorMessage';

// ── Layout ──────────────────────────────────────────────────────────────────

const ArtifactsContainer = styled.div`
  padding: 2rem;
`;

const ArtifactsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 1.5rem;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 8px;
`;

const StatCard = styled.div`
  h4 {
    margin: 0;
    font-size: 0.875rem;
    color: #666;
    text-transform: uppercase;
  }
  p {
    margin: 0.5rem 0 0 0;
    font-size: 1.5rem;
    font-weight: 600;
    color: #0066cc;
  }
`;

const CorrelateButton = styled.button`
  display: block;
  margin-top: 0.75rem;
  padding: 0.4rem 0.9rem;
  background: #0066cc;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  transition: background 0.2s ease;

  &:hover {
    background: #0052a3;
  }
`;

// ── Artifact card ────────────────────────────────────────────────────────────

const ArtifactCard = styled.div`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1.5rem;
  transition: all 0.3s ease;

  &:hover {
    border-color: #0066cc;
    box-shadow: 0 4px 12px rgba(0, 102, 204, 0.1);
  }
`;

const ArtifactName = styled.h4`
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  word-break: break-word;
`;

const ArtifactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 1rem 0;
  font-size: 0.875rem;
  color: #666;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  strong {
    color: #1a1a1a;
    min-width: 100px;
  }
`;

const SHA256Hash = styled.code`
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: 0.75rem;
  background: #f0f0f0;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  word-break: break-all;
  color: #0066cc;
`;

const CardActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const SharedBadge = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.5rem 0.75rem;
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffc107;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  transition: all 0.2s ease;

  &:hover {
    background: #ffc107;
    color: #1a1a1a;
  }
`;

const DownloadButton = styled.button`
  width: 100%;
  padding: 0.6rem;
  background: #0066cc;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.875rem;
  transition: background 0.3s ease;

  &:hover {
    background: #0052a3;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: #999;

  h3 {
    margin-top: 1rem;
    color: #666;
  }

  p {
    margin: 0.5rem 0 0 0;
    font-size: 0.9rem;
  }
`;

// ── Correlated samples section ───────────────────────────────────────────────

const CorrelatedSection = styled.div`
  margin-top: 2rem;
  border-top: 2px solid #e9ecef;
  padding-top: 1.5rem;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  h3 {
    margin: 0;
    font-size: 1.1rem;
    color: #1a1a1a;
  }
`;

const ClearButton = styled.button`
  padding: 0.35rem 0.75rem;
  background: none;
  border: 1px solid #ccc;
  border-radius: 4px;
  color: #666;
  cursor: pointer;
  font-size: 0.85rem;

  &:hover {
    border-color: #999;
    color: #333;
  }
`;

// ── Artifact pivot modal ─────────────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 10px;
  width: 100%;
  max-width: 900px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
  flex-shrink: 0;

  h3 {
    margin: 0 0 0.25rem 0;
    font-size: 1.1rem;
    color: #1a1a1a;
  }

  code {
    font-size: 0.8rem;
    color: #666;
    background: #f0f0f0;
    padding: 0.2rem 0.4rem;
    border-radius: 3px;
  }
`;

const ModalCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #666;
  cursor: pointer;
  padding: 0 0.25rem;
  line-height: 1;
  flex-shrink: 0;

  &:hover {
    color: #1a1a1a;
  }
`;

const ModalBody = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const SampleTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const SampleTh = styled.th`
  padding: 0.75rem 1rem;
  text-align: left;
  background: #f9fafb;
  border-bottom: 2px solid #e5e7eb;
  font-size: 0.8rem;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  position: sticky;
  top: 0;
`;

const SampleTd = styled.td`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f0f0f0;
  font-size: 0.875rem;
  vertical-align: middle;
`;

const SampleLink = styled.button`
  background: none;
  border: none;
  color: #0066cc;
  cursor: pointer;
  padding: 0;
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: 0.8rem;

  &:hover {
    text-decoration: underline;
  }
`;

const FamilyBadge = styled.span`
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${p => p.unknown ? '#f3f4f6' : '#fee2e2'};
  color: ${p => p.unknown ? '#4b5563' : '#991b1b'};
`;

const ModalEmpty = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: #999;
`;

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatFileSize = (bytes) => {
  if (!bytes) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

const formatDate = (dateString) => {
  const d = new Date(dateString);
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString();
};

// ── Component ────────────────────────────────────────────────────────────────

const ArtifactsTab = ({ analysisData, sha256 }) => {
  const navigate = useNavigate();

  // Artifact list state
  const [artifacts, setArtifacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState({});
  const [sharedArtifactsMap, setSharedArtifactsMap] = useState(null);

  // Individual artifact pivot → modal
  const [modalArtifact, setModalArtifact] = useState(null);
  const {
    pivotData: artifactPivotData,
    loading: artifactPivotLoading,
    error: artifactPivotError,
    pivotByArtifact,
    getSharedArtifacts,
    clearPivot: clearArtifactPivot
  } = usePivot();

  // Correlated samples overview → inline section
  const {
    pivotData: correlatedData,
    loading: correlatedLoading,
    error: correlatedError,
    getCorrelatedSamples,
    clearPivot: clearCorrelated
  } = usePivot();

  // ── Data fetching ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (!sha256) return;

    const fetchArtifacts = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`${apiService.baseUrl}/artifacts/apk/${sha256}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
        if (!response.ok) throw new Error('Failed to fetch artifacts');
        setArtifacts(await response.json());
      } catch (err) {
        console.error('Error fetching artifacts:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchSharedMap = async () => {
      const result = await getSharedArtifacts(sha256);
      if (result) setSharedArtifactsMap(result.shared || {});
    };

    fetchArtifacts();
    fetchSharedMap();
  }, [sha256, getSharedArtifacts]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleArtifactPivot = useCallback(async (artifact) => {
    clearArtifactPivot();
    setModalArtifact(artifact);
    try {
      await pivotByArtifact(artifact.sha256);
    } catch (_) {}
  }, [clearArtifactPivot, pivotByArtifact]);

  const handleCloseModal = useCallback(() => {
    setModalArtifact(null);
    clearArtifactPivot();
  }, [clearArtifactPivot]);

  const handleCorrelatedSamples = useCallback(async () => {
    clearCorrelated();
    try {
      await getCorrelatedSamples(sha256);
    } catch (_) {}
  }, [clearCorrelated, getCorrelatedSamples, sha256]);

  const handleDownloadArtifact = async (artifact) => {
    try {
      setDownloading(prev => ({ ...prev, [artifact.sha256]: true }));
      const response = await fetch(
        `${apiService.baseUrl}/artifacts/apk/${sha256}/artifact/${artifact.sha256}/download`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      if (!response.ok) throw new Error('Failed to download artifact');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = artifact.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download error:', err);
      alert('Failed to download artifact');
    } finally {
      setDownloading(prev => ({ ...prev, [artifact.sha256]: false }));
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return <LoadingSpinner>Loading artifacts...</LoadingSpinner>;
  }

  const sharedCount = sharedArtifactsMap ? Object.keys(sharedArtifactsMap).length : 0;

  return (
    <ArtifactsContainer>
      <h3>Extracted Artifacts</h3>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {artifacts.length > 0 ? (
        <>
          {/* Stats bar */}
          <StatsContainer>
            <StatCard>
              <h4>Total Artifacts</h4>
              <p>{artifacts.length}</p>
            </StatCard>
            <StatCard>
              <h4>Total Size</h4>
              <p>{formatFileSize(artifacts.reduce((sum, a) => sum + a.file_size, 0))}</p>
            </StatCard>
            {sharedCount > 0 && (
              <StatCard>
                <h4>Shared Artifacts</h4>
                <p>{sharedCount}</p>
                <CorrelateButton onClick={handleCorrelatedSamples}>
                  Pivot correlated samples
                </CorrelateButton>
              </StatCard>
            )}
          </StatsContainer>

          {/* Artifact cards */}
          <ArtifactsGrid>
            {artifacts.map((artifact) => {
              const count = sharedArtifactsMap?.[artifact.sha256] ?? null;
              return (
                <ArtifactCard key={artifact.sha256}>
                  <ArtifactName title={artifact.filename}>
                    {artifact.filename}
                  </ArtifactName>

                  <ArtifactInfo>
                    <InfoRow>
                      <strong>Size:</strong>
                      <span>{formatFileSize(artifact.file_size)}</span>
                    </InfoRow>
                    <InfoRow>
                      <strong>Path:</strong>
                    </InfoRow>
                    <code style={{ fontSize: '0.75rem', padding: '0.5rem', background: '#f0f0f0', borderRadius: '4px' }}>
                      {artifact.original_path}
                    </code>
                    <InfoRow>
                      <strong>SHA256:</strong>
                    </InfoRow>
                    <SHA256Hash title={artifact.sha256}>
                      {artifact.sha256.substring(0, 16)}...
                    </SHA256Hash>
                    <InfoRow>
                      <strong>Extracted:</strong>
                      <span>{formatDate(artifact.extraction_date)}</span>
                    </InfoRow>
                  </ArtifactInfo>

                  <CardActions>
                    {count !== null && (
                      <SharedBadge onClick={() => handleArtifactPivot(artifact)}>
                        Found in {count} other sample{count !== 1 ? 's' : ''} — see who
                      </SharedBadge>
                    )}
                    <DownloadButton
                      onClick={() => handleDownloadArtifact(artifact)}
                      disabled={downloading[artifact.sha256]}
                    >
                      {downloading[artifact.sha256] ? 'Downloading...' : 'Download'}
                    </DownloadButton>
                  </CardActions>
                </ArtifactCard>
              );
            })}
          </ArtifactsGrid>

          {/* Correlated samples section (overview) */}
          {(correlatedData || correlatedLoading || correlatedError) && (
            <CorrelatedSection>
              <SectionHeader>
                <h3>Correlated Samples</h3>
                <ClearButton onClick={clearCorrelated}>Clear</ClearButton>
              </SectionHeader>
              <PivotResults
                pivotData={correlatedData}
                loading={correlatedLoading}
                error={correlatedError}
                onResultClick={() => {}}
              />
            </CorrelatedSection>
          )}
        </>
      ) : (
        <EmptyState>
          <div style={{ fontSize: '3rem' }}>📁</div>
          <h3>No Artifacts Found</h3>
          <p>This APK analysis did not extract any interesting artifacts.</p>
        </EmptyState>
      )}

      {/* Artifact pivot modal */}
      {modalArtifact && (
        <ModalOverlay onClick={handleCloseModal}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <div>
                <h3>Samples containing this artifact</h3>
                <code>{modalArtifact.filename}</code>
              </div>
              <ModalCloseButton onClick={handleCloseModal}>×</ModalCloseButton>
            </ModalHeader>

            <ModalBody>
              {artifactPivotLoading && (
                <LoadingSpinner>Searching for related samples...</LoadingSpinner>
              )}
              {artifactPivotError && (
                <div style={{ padding: '1rem' }}>
                  <ErrorMessage>{artifactPivotError}</ErrorMessage>
                </div>
              )}
              {artifactPivotData && !artifactPivotLoading && (
                artifactPivotData.results?.length > 0 ? (
                  <>
                    <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e9ecef', fontSize: '0.9rem', color: '#555' }}>
                      {artifactPivotData.total_matches} sample{artifactPivotData.total_matches !== 1 ? 's' : ''} contain this artifact
                    </div>
                    <SampleTable>
                      <thead>
                        <tr>
                          <SampleTh>App</SampleTh>
                          <SampleTh>SHA256</SampleTh>
                          <SampleTh>Package</SampleTh>
                          <SampleTh>Family</SampleTh>
                        </tr>
                      </thead>
                      <tbody>
                        {artifactPivotData.results.map(result => (
                          <tr key={result.sha256}>
                            <SampleTd>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                {result.icon && (
                                  <img
                                    src={`data:image/png;base64,${result.icon}`}
                                    alt=""
                                    width={28}
                                    height={28}
                                    style={{ borderRadius: 4, flexShrink: 0 }}
                                    onError={e => { e.target.style.display = 'none'; }}
                                  />
                                )}
                                <span style={{ fontWeight: 500, color: '#1a1a1a' }}>
                                  {result.app_name || '—'}
                                </span>
                              </div>
                            </SampleTd>
                            <SampleTd>
                              <SampleLink
                                onClick={() => { navigate(`/analysis/${result.sha256}`); handleCloseModal(); }}
                                title={result.sha256}
                              >
                                {result.sha256.substring(0, 12)}...
                              </SampleLink>
                            </SampleTd>
                            <SampleTd style={{ color: '#555', fontSize: '0.8rem' }}>
                              {result.package_name || '—'}
                            </SampleTd>
                            <SampleTd>
                              <FamilyBadge unknown={!result.malware_family || result.malware_family === 'Unknown'}>
                                {result.malware_family || 'Unknown'}
                              </FamilyBadge>
                            </SampleTd>
                          </tr>
                        ))}
                      </tbody>
                    </SampleTable>
                  </>
                ) : (
                  <ModalEmpty>
                    <p>No other samples contain this artifact.</p>
                  </ModalEmpty>
                )
              )}
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </ArtifactsContainer>
  );
};

export default ArtifactsTab;
